import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Zap, Radio, Users, Calendar as CalendarIcon, Clock, Copy, Check } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

type LessonMode = 'live' | 'student-paced';

interface StartLessonModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lessonTitle: string;
  mode: LessonMode;
  onStart: (config: { dueDate?: Date; classes?: string[]; sessionCode?: string }) => void;
}

export function StartLessonModal({ 
  open, 
  onOpenChange, 
  lessonTitle, 
  mode,
  onStart 
}: StartLessonModalProps) {
  const [sessionCode] = useState(() => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  });
  const [dueDate, setDueDate] = useState<Date>();
  const [dueTime, setDueTime] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(sessionCode);
    setCopied(true);
    toast.success('Session code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStart = () => {
    if (mode === 'student-paced' && (!dueDate || !dueTime)) {
      toast.error('Please select a due date and time');
      return;
    }

    let dueDateTime: Date | undefined;
    if (mode === 'student-paced' && dueDate && dueTime) {
      const [hours, minutes] = dueTime.split(':').map(Number);
      dueDateTime = new Date(dueDate);
      dueDateTime.setHours(hours || 0, minutes || 0, 0, 0);
    }

    onStart({
      dueDate: mode === 'student-paced' ? dueDateTime : undefined,
      sessionCode: sessionCode, // Pass session code for both modes
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {mode === 'live' ? 'Start Live Session' : 'Assign Lesson'}
          </DialogTitle>
          <DialogDescription>
            {lessonTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Mode Badge */}
          <div className="flex items-center gap-2">
            <Badge 
              className={`rounded-lg px-3 py-1 ${
                mode === 'live' 
                  ? 'bg-emerald-100 text-emerald-700 border-emerald-200' 
                  : 'bg-blue-100 text-blue-700 border-blue-200'
              }`}
              variant="outline"
            >
              {mode === 'live' ? (
                <>
                  <Zap className="w-3 h-3 mr-1" />
                  LIVE SESSION
                </>
              ) : (
                <>
                  <Radio className="w-3 h-3 mr-1" />
                  STUDENT-PACED
                </>
              )}
            </Badge>
          </div>

          {/* Live Session Mode */}
          {mode === 'live' && (
            <>
              <div className="bg-accent/50 border border-border rounded-xl p-6 space-y-4">
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">Session Code</p>
                  <div className="flex items-center justify-center gap-2">
                    <code className="text-4xl font-bold tracking-wider font-mono bg-background px-6 py-3 rounded-xl border-2 border-primary">
                      {sessionCode}
                    </code>
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={handleCopyCode}
                      className="rounded-xl h-12 w-12"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="pt-4 border-t border-border space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-emerald-600" />
                    <span>Real-time control enabled</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-primary" />
                    <span>Students will follow your slides live</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <span>Session starts when you click "Start"</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-900">
                  <strong>Tip:</strong> Share the session code with students so they can join at{' '}
                  <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">
                    https://edulive.fun/join
                  </code>
                </p>
              </div>
            </>
          )}

          {/* Student-Paced Mode */}
          {mode === 'student-paced' && (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left rounded-xl"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dueDate ? format(dueDate, 'PPP') : 'Select due date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={dueDate}
                        onSelect={(selected) => {
                          setDueDate(selected);
                          if (selected && !dueTime) {
                            setDueTime('23:59');
                          }
                        }}
                        initialFocus
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Due Time *</Label>
                  <Input
                    type="time"
                    value={dueTime}
                    onChange={(e) => setDueTime(e.target.value)}
                    className="rounded-xl"
                  />
                </div>

                {/* Session Code for Student-Paced */}
                <div className="bg-accent/50 border border-border rounded-xl p-6 space-y-4">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">Access Code</p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="text-4xl font-bold tracking-wider font-mono bg-background px-6 py-3 rounded-xl border-2 border-primary">
                        {sessionCode}
                      </code>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={handleCopyCode}
                        className="rounded-xl h-12 w-12"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-emerald-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Radio className="w-4 h-4 text-blue-600" />
                      <span>Students progress independently</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4 text-primary" />
                      <span>Free navigation through slides</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-orange-600" />
                      <span>Can resume anytime before due date and time</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-900">
                  <strong>Tip:</strong> Share the access code with students so they can join at{' '}
                  <code className="bg-white px-2 py-0.5 rounded font-mono text-xs">
                    app.edulive.com/join
                  </code>
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={handleStart}
            className={`rounded-xl ${
              mode === 'live' 
                ? 'bg-emerald-600 hover:bg-emerald-700' 
                : 'bg-primary hover:bg-primary/90'
            }`}
          >
            {mode === 'live' ? (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Start Live Now
              </>
            ) : (
              <>
                <Users className="w-4 h-4 mr-2" />
                Assign to Students
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}